import { useEffect, useRef, useState } from 'react';
import { interpretRows, parseCsv, addressMatches, type ImportRow } from '../../app/csvImport';
import { candidateToBuilding, findEgid, searchSwiss, type Candidate } from '../../geo/services';
import type { Building } from '../../core/domain/types';
import { useApp } from '../../app/store';
import { MAX_BUILDINGS } from '../../app/persist';
import { fmt } from '../../app/format';
import { IconClose, IconUpload } from '../icons';

type RowState = { row: ImportRow; status: 'offen' | 'gefunden' | 'prüfen' | 'nicht gefunden' | 'ungültig' | 'fehler'; note?: string; building?: Building; include: boolean };

const SAMPLE = `Bezeichnung;Adresse;EGID;Lat;Lon
Schulhaus Beispiel;Rämistrasse 101, 8006 Zürich;;;
Verwaltung;;;46.9480;7.4474
Werkhof;;3169989;;`;

async function resolve(row: ImportRow): Promise<Omit<RowState, 'row' | 'include'>> {
  const r = row.req;
  if (r.kind === 'invalid') return { status: 'ungültig', note: r.reason };
  try {
    let c: Candidate | undefined;
    let status: RowState['status'] = 'gefunden';
    let note: string | undefined;
    if (r.kind === 'coords') {
      c = { id: `csv-${row.line}`, source: 'koordinaten', kind: 'Koordinaten', label: row.label, sub: '', lat: r.lat, lon: r.lon };
    } else if (r.kind === 'egid') {
      c = (await findEgid(r.egid))[0];
      if (!c) return { status: 'nicht gefunden', note: `EGID ${r.egid} nicht im GWR` };
    } else {
      const hits = (await searchSwiss(r.text)).filter((h) => h.kind === 'Adresse');
      c = hits[0];
      if (!c) return { status: 'nicht gefunden', note: 'Keine amtliche Adresse gefunden' };
      if (!addressMatches(r.text, c.label)) {
        status = 'prüfen';
        note = `Nächster Treffer: ${c.label}`;
      } else note = c.label;
    }
    const b = await candidateToBuilding(c, row.label);
    if (r.kind === 'coords') b.geocoder = 'csv-koordinaten';
    return { status, note, building: b };
  } catch (e) {
    return { status: 'fehler', note: (e as Error).message };
  }
}

export function CsvImportDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<RowState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const cancel = useRef(false);
  const addBuildings = useApp((s) => s.addBuildings);
  const notify = useApp((s) => s.notify);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  const start = async (src: string) => {
    setError(null);
    const parsed = interpretRows(parseCsv(src));
    if (parsed.error) {
      setError(parsed.error);
      setRows([]);
      return;
    }
    const init: RowState[] = parsed.rows.map((row) => ({ row, status: row.req.kind === 'invalid' ? 'ungültig' : 'offen', note: row.req.kind === 'invalid' ? row.req.reason : undefined, include: false }));
    setRows(init);
    setRunning(true);
    cancel.current = false;
    const out = [...init];
    let next = 0;
    const worker = async () => {
      while (!cancel.current && next < out.length) {
        const i = next++;
        if (out[i].status === 'ungültig') continue;
        const res = await resolve(out[i].row);
        out[i] = { ...out[i], ...res, include: res.status === 'gefunden' || res.status === 'prüfen' };
        setRows([...out]);
      }
    };
    await Promise.all(Array.from({ length: 6 }, worker));
    setRunning(false);
  };

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > 2_000_000) {
      setError('Datei grösser als 2 MB.');
      return;
    }
    const t = await f.text();
    setText(t);
    void start(t);
  };

  const take = () => {
    const bs = rows.filter((r) => r.include && r.building).map((r) => r.building!);
    const { added, skipped, overLimit } = addBuildings(bs);
    const parts = [
      skipped ? `${skipped} bereits im Portfolio vorhandene Einträge übersprungen.` : '',
      overLimit ? `${overLimit} Einträge nicht übernommen: Ein Portfolio fasst höchstens ${fmt(MAX_BUILDINGS, 0)} Gebäude.` : '',
    ].filter(Boolean);
    notify(overLimit ? 'warn' : 'info', `${added} Gebäude übernommen`, parts.length ? parts.join(' ') : undefined);
    setRows([]);
    setText('');
    onClose();
  };

  const counts = rows.reduce<Record<string, number>>((a, r) => ((a[r.status] = (a[r.status] ?? 0) + 1), a), {});
  const done = rows.filter((r) => r.status !== 'offen').length;

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby="csv-title"
      onClose={() => {
        cancel.current = true;
        onClose();
      }}
    >
      <div className="dialog-head">
        <h2 id="csv-title">Gebäude aus CSV importieren</h2>
        <button type="button" className="icon-btn" aria-label="Dialog schliessen" onClick={() => ref.current?.close()}>
          <IconClose />
        </button>
      </div>
      <div className="dialog-body stack" style={{ gap: 16 }}>
        <p className="small muted">
          Erste Zeile mit Spaltennamen. Erkannt werden Bezeichnung, Adresse (oder Strasse, Nr, PLZ, Ort), EGID, Lat/Lon (WGS84) oder E/N (LV95). Trennzeichen Semikolon, Komma oder Tabulator. Adressen und EGID werden direkt bei geo.admin.ch aufgelöst; es
          werden keine Daten an den Server dieser App übermittelt. Höchstens 1’000 Zeilen.
        </p>
        <div className="row">
          <label className="btn">
            <IconUpload /> CSV-Datei wählen
            <input type="file" accept=".csv,.txt,text/csv" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} />
          </label>
          <button type="button" className="btn btn-ghost" onClick={() => setText(SAMPLE)}>
            Beispiel einfügen
          </button>
        </div>
        <div className="field">
          <label htmlFor="csv-text">Oder Inhalt einfügen</label>
          <textarea id="csv-text" className="input" style={{ minHeight: 120, padding: 12, fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 14 }} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
        <div className="row">
          <button type="button" className="btn btn-primary" disabled={!text.trim() || running} onClick={() => void start(text)}>
            Zeilen prüfen
          </button>
          {running && (
            <span className="small muted" role="status">
              Prüfe Zeile {done} von {rows.length} …
            </span>
          )}
        </div>
        {error && (
          <div className="notice notice-danger" role="alert">
            <div className="notice-body">{error}</div>
          </div>
        )}
        {rows.length > 0 && (
          <>
            <p className="small" role="status">
              {rows.length} Zeilen · gefunden {counts['gefunden'] ?? 0} · prüfen {counts['prüfen'] ?? 0} · nicht gefunden {counts['nicht gefunden'] ?? 0} · ungültig {counts['ungültig'] ?? 0}
              {counts['fehler'] ? ` · Fehler ${counts['fehler']}` : ''}
            </p>
            <div className="table-wrap" style={{ maxHeight: 320 }} tabIndex={0} role="region" aria-label="Importvorschau">
              <table className="data">
                <thead>
                  <tr>
                    <th>Übernehmen</th>
                    <th className="n">Zeile</th>
                    <th>Bezeichnung</th>
                    <th>Status</th>
                    <th>Ergebnis</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.row.line}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Zeile ${r.row.line} übernehmen`}
                          checked={r.include}
                          disabled={!r.building}
                          onChange={(e) => setRows(rows.map((x, k) => (k === i ? { ...x, include: e.target.checked } : x)))}
                        />
                      </td>
                      <td className="n">{r.row.line}</td>
                      <td>{r.row.label}</td>
                      <td>
                        <span className={`chip ${r.status === 'gefunden' ? 'chip-live' : r.status === 'prüfen' ? 'chip-warn' : r.status === 'offen' ? '' : 'chip-danger'}`}>{r.status}</span>
                      </td>
                      <td className="small">{r.note ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      <div className="dialog-foot">
        <button type="button" className="btn" onClick={() => ref.current?.close()}>
          Abbrechen
        </button>
        <button type="button" className="btn btn-primary" disabled={running || !rows.some((r) => r.include)} onClick={take}>
          {rows.filter((r) => r.include).length} Gebäude übernehmen
        </button>
      </div>
    </dialog>
  );
}
